/**
 * Secure Amplitude Tracking Service
 * 
 * Proxies all tracking events through backend to fix insecure cookie vulnerabilities.
 * Backend handles authentication, user identification, and forwards to Amplitude with
 * HttpOnly cookies and CSRF protection.
 */

const API_BASE = `${process.env.REACT_APP_API_URL}/v1/amplitude/`;

const getCsrfToken = (): string | undefined => {
    if (typeof document === 'undefined') return undefined;
    
    const match = document.cookie
        .split(';')
        .map(c => c.trim())
        .find(c => c.startsWith('csrftoken='));
    
    return match ? match.split('=').slice(1).join('=') : undefined;
};

/**
 * Returns browser-specific metadata unavailable to backend.
 * Backend automatically captures: user_id, IP, User-Agent, device_id, session_id.
 */
const getClientMetadata = () => {
    return {
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
    };
};

/**
 * Initialize tracking on app startup.
 * Sends session_start event and establishes device_id cookie.
 */
const init = async () => {
    try {
        const csrfToken = getCsrfToken();
        
        const response = await fetch(`${API_BASE}track/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
            },
            body: JSON.stringify({
                event_type: 'session_start',
                event_properties: {
                    ...getClientMetadata(),
                },
                platform: 'Web',
                app_version: process.env.REACT_APP_VERSION || '3.0.71',
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Failed to initialize tracking:', errorData);
        }
    } catch (error) {
        console.error('Error initializing tracking:', error);
    }
};

/**
 * Track an analytics event.
 * 
 * @param eventType - Event name (e.g., "Module Clicked", "User Logged In")
 * @param eventProperties - Event-specific data
 * @param userProperties - User attributes for behavioral tracking (e.g., preferences, milestones)
 * 
 * Note: Backend extracts user_id from session. Never send sensitive data from frontend.
 */
const track = async (
    eventType: string, 
    eventProperties?: Record<string, any>,
    userProperties?: Record<string, any>
) => {
    try {
        const csrfToken = getCsrfToken();
        
        const response = await fetch(`${API_BASE}track/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
            },
            body: JSON.stringify({
                event_type: eventType,
                event_properties: {
                    ...(eventProperties || {}),
                    ...getClientMetadata(),
                },
                ...(userProperties ? { user_properties: userProperties } : {}),
                platform: 'Web',
                app_version: process.env.REACT_APP_VERSION || '3.0.71',
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Failed to track event:', eventType, errorData);
        }
    } catch (error) {
        console.error('Error tracking event:', error);
    }
};

/**
 * Track page navigation event.
 */
const trackPageView = async (pageName: string, additionalProperties?: Record<string, any>) => {
    await track('Page View', {
        page: pageName,
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer,
        ...additionalProperties,
    });
};

export const trackingService = {
    init,
    track,
    trackPageView,
};
